//
//  AssumedBanner.tsx
//  TrajeXY Signet Server
//
//  Copyright © 2026 TrajeXY. All rights reserved.
//
//  Created: September 23, 2026
//  Author: Bob Keathley
//
//  Non-dismissible tenant-app banner shown while a platform operator is assuming
//  this tenant (RB-13), read from the X-Signet-Assumed header. The session is
//  recorded, so the banner cannot be hidden.
//
import { useSyncExternalStore } from 'react'
import { getAssumed, subscribeAssumed } from '../api/client'
import type { Session } from '../auth'

/**
 * The tenant app's non-dismissible banner while a platform operator is looking at this tenant
 * under `POST /v1/platform/assume` (27 §8a). Renders from `X-Signet-Assumed` on the last response;
 * there is nothing to dismiss because there is nothing to hide -- "this session is recorded" is
 * literal (the tenant's own `assumed_presence` row, per RB-13).
 */
export default function AssumedBanner({ session }: { session: Session }) {
  const assumed = useSyncExternalStore(subscribeAssumed, getAssumed, getAssumed)
  if (!assumed) return null
  const mins = Math.max(0, Math.round((assumed.expiresAt * 1000 - Date.now()) / 60000))
  return (
    <div className="banner assumed" role="status">
      viewing tenant {session.user.tenant_id} as <b>{assumed.email}</b>
      {assumed.mode === 'read' ? ', read-only' : ''}, expires in {mins}m, this session is recorded
    </div>
  )
}
